import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSSEAwareQuery } from './useSSEAwareQuery';
import { apiService, Profile, ColumnProfile } from '../services/api';
import {
  TableSummary,
  ColumnSummary,
  JoinDefinition,
  UnifiedTableData,
  UnifiedColumnData,
} from '../types/unifiedSchema';

// ── Normalization helpers (ported from SemanticAnalysisPanel) ──

const isColumnInList = (colName: string, candidates?: string[]): boolean => {
  if (!candidates || !Array.isArray(candidates)) return false;
  const normalizedName = colName.toLowerCase().trim();
  return candidates.some(
    (c) => typeof c === 'string' && c.toLowerCase().trim() === normalizedName
  );
};

const normalizeSemanticRole = (role: string | null | undefined): string => {
  if (!role || typeof role !== 'string') return 'unknown';
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  return normalized || 'unknown';
};

const generateDescription = (col: ColumnSummary): string => {
  if (col.description && col.description.trim() && col.description !== 'null') {
    return col.description;
  }
  return '';
};

const normalizeColumn = (col: ColumnSummary, table: TableSummary): ColumnSummary => ({
  ...col,
  semantic_role: normalizeSemanticRole(col.semantic_role),
  description: generateDescription(col),
  is_primary_key: col.is_primary_key ?? isColumnInList(col.name, table.primary_key_candidates),
  is_foreign_key: col.is_foreign_key ?? isColumnInList(col.name, table.foreign_key_candidates),
});

const normalizeJoin = (join: any): JoinDefinition | null => {
  if (typeof join === 'string') return null;
  if (!join || typeof join !== 'object') return null;
  return {
    to: join.to || '',
    on: Array.isArray(join.on) ? join.on : join.on ? [join.on] : [],
    join_type: join.join_type || 'shared_key',
    confidence: typeof join.confidence === 'number' ? join.confidence : 0.5,
    description: join.description || '',
  };
};

const normalizeTableSummary = (table: TableSummary): TableSummary => ({
  ...table,
  column_summaries: (table.column_summaries || []).map((col) => normalizeColumn(col, table)),
  likely_joins: (table.likely_joins || [])
    .map((j: any) => normalizeJoin(j))
    .filter((j): j is JoinDefinition => j !== null),
});

// ── Merge logic ──
// Match profiles to semantic summaries using the `file_origin` field.
// The semantic summary pipeline slugifies file names (edc_ae_log.csv → edc-ae-log)
// but preserves the original source identifier in `file_origin`.
// The source could be a CSV file, Excel sheet name, database table name, etc.

function findProfile(
  table: TableSummary,
  profileByFileName: Map<string, Profile>,
): Profile | undefined {
  // 1. Primary: use file_origin (the original source identifier preserved from profiling)
  //    This is the most reliable match — e.g., "edc_ae_log.csv" matches exactly
  if (table.file_origin) {
    const byOrigin = profileByFileName.get(table.file_origin);
    if (byOrigin) return byOrigin;
  }
  // 2. Fallback: try the slugified file_name (works when profile also has a slug)
  const bySlug = profileByFileName.get(table.file_name);
  if (bySlug) return bySlug;
  return undefined;
}

function mergeTableData(
  summaries: TableSummary[],
  profiles: Profile[]
): UnifiedTableData[] {
  // Index profiles by their file_name (the original source name from profiling)
  const profileByFileName = new Map<string, Profile>();
  for (const p of profiles) {
    profileByFileName.set(p.file_name, p);
  }

  return summaries.map((table) => {
    const profile = findProfile(table, profileByFileName);

    // Build a column-name→profile lookup (try both column_name and name fields)
    const colProfileMap = new Map<string, ColumnProfile>();
    if (profile) {
      for (const cp of profile.columns) {
        const key = (cp.column_name || (cp as any).name || '').toLowerCase().trim();
        if (key) colProfileMap.set(key, cp);
      }
    }

    const columns: UnifiedColumnData[] = (table.column_summaries || []).map((col) => {
      const cp = colProfileMap.get(col.name.toLowerCase().trim());
      // Prefer the profile's original_name (raw source header) for display,
      // falling back to the semantic column's own original_name, then to
      // the canonical sanitized name.
      const displayOriginal =
        (cp as any)?.original_name || (col as any).original_name || col.name;
      return {
        name: col.name,
        original_name: displayOriginal,
        // Statistical (from profile, fallback to semantic column data_type)
        data_type: cp?.data_type || (cp as any)?.inferred_type || col.data_type || 'unknown',
        null_percentage: cp?.null_percentage ?? 0,
        unique_count: cp?.unique_count ?? 0,
        sample_values: cp?.sample_values ?? [],
        min_value: cp?.min_value,
        max_value: cp?.max_value,
        avg_length: cp?.avg_length,
        // Semantic
        semantic_role: col.semantic_role,
        description: col.description,
        pii_likelihood: col.pii_likelihood,
        is_primary_key: col.is_primary_key,
        is_foreign_key: col.is_foreign_key,
        fk_references: col.fk_references,
      };
    });

    return {
      file_name: table.file_name,
      file_origin: table.file_origin,
      semantic_name: table.semantic_name || table.file_name,
      description: table.description || '',
      entity_type: table.entity_type || 'unknown',
      row_count: profile?.row_count ?? 0,
      file_size_mb: profile?.file_size_mb ?? 0,
      primary_key_candidates: table.primary_key_candidates || [],
      foreign_key_candidates: table.foreign_key_candidates || [],
      likely_joins: table.likely_joins || [],
      columns,
    };
  });
}

// ── Hook ──

export function useUnifiedSchema(batchId: string) {
  const queryClient = useQueryClient();

  // Summaries state (authoritative, editable)
  const [summaries, setSummaries] = useState<TableSummary[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const isInitializingRef = useRef(true);

  // Column edit dialog state
  const [editingColumn, setEditingColumn] = useState<{
    tableIndex: number;
    colIndex: number;
    column: ColumnSummary;
  } | null>(null);

  // ── Queries ──

  const { data: batch } = useSSEAwareQuery(
    ['batch', batchId],
    () => apiService.getBatch(batchId),
    { enabled: !!batchId, pollingInterval: 5_000, ssePollingInterval: 30_000 }
  );

  const {
    data: semanticData,
    isLoading: isSemanticLoading,
    error: semanticError,
    refetch: refetchSemantic,
  } = useQuery(
    ['semantic-summaries', batchId],
    () => apiService.getSemanticSummaries(batchId),
    {
      enabled: !!batchId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        isInitializingRef.current = true;
        const rawList = (Array.isArray(data) ? data : (data as any).summaries || []) as unknown as TableSummary[];
        const normalizedList = rawList.map((table) => normalizeTableSummary(table));
        setSummaries([...normalizedList]);
        setHasChanges(false);
        requestAnimationFrame(() => {
          isInitializingRef.current = false;
        });
      }
    }
  );

  const {
    data: profiles,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useQuery(
    ['profile', batchId],
    () => apiService.getProfile(batchId),
    { enabled: !!batchId, refetchOnWindowFocus: false }
  );

  const { data: mappingStatus } = useQuery(
    ['mapping-status', batchId],
    () => apiService.getMappings(batchId),
    { enabled: !!batchId, refetchOnWindowFocus: false, retry: false, onError: () => {} }
  );


  // ── Merged data ──

  const mergedTables = useMemo(
    () => mergeTableData(summaries, profiles || []),
    [summaries, profiles]
  );

  // ── Mutations ──

  const saveMutation = useMutation(
    () => apiService.updateSemanticSummary(batchId, { summaries }),
    {
      onSuccess: () => {
        setHasChanges(false);
        queryClient.invalidateQueries(['semantic-summaries', batchId]);
      },
    }
  );

  const triggerMappingMutation = useMutation(
    async () => {
      await apiService.updateSemanticSummary(batchId, { summaries });
      await apiService.triggerBatchMapping(batchId);
    },
    {
      onSuccess: () => {
        setHasChanges(false);
        queryClient.invalidateQueries(['batch', batchId]);
        // Invalidate any stale cached mapping data so the Mapping tab
        // fetches fresh results once the async generation completes.
        queryClient.invalidateQueries(['mappings', batchId]);
        queryClient.invalidateQueries(['mapping-status', batchId]);
      },
    }
  );

  // ── Handlers ──

  const handleUpdateTable = useCallback(
    (index: number, field: keyof TableSummary, value: string) => {
      setSummaries((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
      if (!isInitializingRef.current) setHasChanges(true);
    },
    []
  );

  const handleUpdateColumn = useCallback(
    (tableIndex: number, colIndex: number, updates: Partial<ColumnSummary>) => {
      setSummaries((prev) => {
        const next = [...prev];
        const table = { ...next[tableIndex] };
        const columns = [...(table.column_summaries || [])];
        columns[colIndex] = { ...columns[colIndex], ...updates };
        table.column_summaries = columns;
        next[tableIndex] = table;
        return next;
      });
      if (!isInitializingRef.current) setHasChanges(true);
    },
    []
  );

  const handleTogglePrimaryKey = useCallback(
    (tableIndex: number, columnName: string, isPk: boolean) => {
      setSummaries((prev) => {
        const next = [...prev];
        const table = { ...next[tableIndex] };

        const candidates = [...(table.primary_key_candidates || [])];
        if (isPk && !candidates.some((c) => c.toLowerCase() === columnName.toLowerCase())) {
          candidates.push(columnName);
        } else if (!isPk) {
          const idx = candidates.findIndex((c) => c.toLowerCase() === columnName.toLowerCase());
          if (idx >= 0) candidates.splice(idx, 1);
        }
        table.primary_key_candidates = candidates;

        const columns = [...(table.column_summaries || [])];
        const colIdx = columns.findIndex((c) => c.name === columnName);
        if (colIdx >= 0) {
          columns[colIdx] = { ...columns[colIdx], is_primary_key: isPk };
          table.column_summaries = columns;
        }

        next[tableIndex] = table;
        return next;
      });
      if (!isInitializingRef.current) setHasChanges(true);
    },
    []
  );

  const handleUpdateJoins = useCallback(
    (tableIndex: number, updatedJoins: JoinDefinition[]) => {
      setSummaries((prev) => {
        const next = [...prev];
        next[tableIndex] = { ...next[tableIndex], likely_joins: updatedJoins };
        return next;
      });
      if (!isInitializingRef.current) setHasChanges(true);
    },
    []
  );

  // FK reference options for column edit dialog
  const fkReferenceOptions = useMemo(() => {
    const options: string[] = [];
    summaries.forEach((table) => {
      const tableName = table.semantic_name || table.file_name;
      (table.column_summaries || []).forEach((col) => {
        options.push(`${tableName}.${col.name}`);
      });
    });
    return options;
  }, [summaries]);

  // ── Actions ──

  const handleSave = useCallback(() => saveMutation.mutate(), [saveMutation]);

  const handleProceedToMapping = useCallback(
    () => triggerMappingMutation.mutate(),
    [triggerMappingMutation]
  );

  // Async variant used when the caller needs to await completion
  const handleProceedToMappingAsync = useCallback(
    () => triggerMappingMutation.mutateAsync(),
    [triggerMappingMutation]
  );

  // const refetch = useCallback(() => {
  //   refetchSemantic();
  //   refetchProfile();
  // }, [refetchSemantic, refetchProfile]);
  const refetch = useCallback(async () => {
  await Promise.all([
    refetchSemantic(),
    refetchProfile(),
  ]);
}, [refetchSemantic, refetchProfile]);

  // Column edit helpers
  const openColumnEditor = useCallback(
    (tableIndex: number, colIndex: number, column: ColumnSummary) => {
      setEditingColumn({ tableIndex, colIndex, column: { ...column } });
    },
    []
  );

  const closeColumnEditor = useCallback(() => setEditingColumn(null), []);

  const saveColumnEdit = useCallback(() => {
    if (editingColumn) {
      handleUpdateColumn(editingColumn.tableIndex, editingColumn.colIndex, editingColumn.column);
      closeColumnEditor();
    }
  }, [editingColumn, handleUpdateColumn, closeColumnEditor]);

  const isLoading = isSemanticLoading || isProfileLoading;
  const isSaving = saveMutation.isLoading || triggerMappingMutation.isLoading;
  const mappingAlreadyStarted =
    batch?.status &&
    ['mapping', 'mapped', 'reviewing', 'approved', 'transforming', 'loading', 'completed'].includes(
      batch.status
    );

  return {
    // Data
    summaries,
    mergedTables,
    profiles: profiles || [],
    batch,
    mappingStatus,
    // State
    isLoading,
    isSaving,
    hasChanges,
    semanticError,
    saveMutationSuccess: saveMutation.isSuccess,
    mappingAlreadyStarted,
    // Column edit
    editingColumn,
    setEditingColumn,
    openColumnEditor,
    closeColumnEditor,
    saveColumnEdit,
    fkReferenceOptions,
    // Handlers
    handleUpdateTable,
    handleUpdateColumn,
    handleTogglePrimaryKey,
    handleUpdateJoins,
    handleSave,
    handleProceedToMapping,
    handleProceedToMappingAsync,
    refetch,
  };
}

export { isColumnInList };
